import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { RecursiveShapeType } from '../../utils/recursiveShapeType';
import TreeFuncs from './TreeFuncs';
import * as R from 'ramda';
import '@patternfly/react-core/dist/styles/base.css';
import {
  Skeleton,
  Toolbar,
  ToolbarContent,
  ToolbarItem,
  TreeView,
  TreeViewSearch
} from '@patternfly/react-core';
import './Tree.css';
import _ from 'lodash';
import { NodeType, NodeRelationshipType } from '../../utils/types';

const updateExpand = R.curry(function updateExpand(expand, node) {
  if (node) {
    return {
      ...node,
      defaultExpanded: expand
    };
  }
});

const check = R.curry(function check(check, item, node) {
  if (node?.id === item?.id && node.parent?.id === item.parent?.id) {
    return {
      ...node,
      checkProps: { checked: check }
    };
  }
  return node;

});


const filterNodeName = R.curry(function (name, node) {
  let first = [];
  let keepNode = false;
  if (!_.isEmpty(node.displayName)) {
    first = _.find(node.assetDescendants, (node) => {
      if (node?.displayName) {
        return (node.displayName.toLowerCase().indexOf(name) !== -1);
      } else if (node?.name) {
        return (node.name.toLowerCase().indexOf(name) !== -1);
      }
      return false;
    });
    keepNode = (node.displayName.toLowerCase().indexOf(name.toLowerCase()) !== -1);
  } else if (!_.isEmpty(node.name)) {
    first = _.find(node.assetDescendants, (node) => {
      return (node.name?.toLowerCase().indexOf(name) !== -1);
    });
    keepNode = (node.name.toLowerCase().indexOf(name.toLowerCase()) !== -1);
  }
  if (!_.isEmpty(first) || keepNode) {
    return node;
  }
});


export const getChildResources = (type, item) => {
  return item?.children.filter(i => i.type === type);
};

export const SELECT_MODE = Object.freeze({
  BUILD: 'build',
  SINGLE: 'single',
  MULTI: 'multi'
});

/**
 *
 * @param {*} tree - the fully populated tree to render
 * @param {*} activeItems - item that should be selected in tree (resource or path to resource)
 * @param {} onSelect - callback function with resources selected, one resource in SINGLE or list in MULTI
 * @param {boolean} isLoading - loading flag for tree visualisation
 * @param {Nod[]} relationFilter - list of relations to render in tree
 * @param {NodeType[]} nodeFilter - list of node types to render
 * @param {SELECT_MODE} selectMode - how selection works in the tree
 * @param {NodeType[]} allowedSelection - lists which TREE_NODE types that are allowed to select in the tree
 * @param {*} onError - callback if error state is found
 * @returns JSX.Element
 */

const Tree = ({ tree, activeItems: initialActiveItems, onSelect, isLoading, relationFilter, nodeFilter, selectMode = SELECT_MODE.SINGLE, allowedSelection = [NodeType.Asset, NodeType.Node], onError }) => {
  const [activeItems, setActiveItems] = useState(initialActiveItems);
  const [resourceList, setResourceList] = useState([]);
  const [filteredItems, setFilteredItems] = useState(tree ? _.compact(tree) : []);
  // TODO: use state?
  const [, setIsFiltered] = useState(false);
  // TODO: use state?
  const [, setRelationFilter] = useState([NodeRelationshipType.Contains, null, undefined, NodeRelationshipType.DependsOn]);
  // TODO: use state?
  const [, setNodeFilter] = useState([NodeType.Node, NodeType.Dashboard, NodeType.Asset]);
  const [internalTree, setInternalTree] = useState([]);
  const selection = useRef([]);
  const [initialProcessingReady, setInitialProcessingReady] = useState(false);

  useEffect(() => {
    setActiveItems(initialActiveItems);
    selection.current = initialActiveItems;
  }, [initialActiveItems]);

  const onCheck = useCallback((unusedEvent, item) => {
    if (_.findIndex(allowedSelection, val => val === item.type) !== -1) {
      selection.current = _.xorBy(selection.current, [item], val => val.id);
      const filtered = _.cloneDeep(filteredItems).map(
        R.compose(
          TreeFuncs.map(check(_.includes(selection.current, item), item))
        )
      );
      setActiveItems([item]);
      setFilteredItems(filtered);
      onSelect?.(selection.current);
    } else {
      // TODO: Translation
      onError?.('Not allowed selection');
    }
  }, [allowedSelection, filteredItems, onError, onSelect]);

  const _onSelect = useCallback((unusedEvent, item) => {
    let i;
    switch (selectMode) {
      case SELECT_MODE.SINGLE:
        i = resourceList.filter(i => (i.id === item.id) && (i.parent?.id === item.parent?.id));
        setActiveItems([item]);
        onSelect(i[0]);
        break;
      case SELECT_MODE.MULTI:
        console.error('should not be possible to have multi-select');
        break;
      case SELECT_MODE.BUILD:
        setActiveItems([item]);
        onSelect(item);
        break;
      default:
        console.error('no SelectMode defined');
    }
  }, [onSelect, resourceList, selectMode]);


  useEffect(function updateFilters() {
    if (!_.isEmpty(relationFilter)) {
      setRelationFilter([...relationFilter, null, undefined]);
    }
    if (!_.isEmpty(nodeFilter)) {
      setNodeFilter([...nodeFilter]);
    }
  }, [nodeFilter, relationFilter]);


  useEffect(() => {
    if (_.isEmpty(_.compact(tree))) {
      return;
    }

    switch (selectMode) {
      case SELECT_MODE.SINGLE:
      case SELECT_MODE.MULTI:
        setResourceList([tree[0], ...tree[0]?.assetDescendants]);
        setInternalTree(tree);
        setFilteredItems(tree);
        setInitialProcessingReady(true);
        break;
      case SELECT_MODE.BUILD:
        const expanded = _.cloneDeep(tree).map(
          R.compose(
            TreeFuncs.map(updateExpand(true))
          )
        );
        setInternalTree(expanded);
        setFilteredItems(expanded);
        setInitialProcessingReady(true);
        break;
      default:
        break;
    }
  }, [tree, selectMode]);

  const onTreeSearch = useCallback((evt) => {
    const input = evt.target.value;
    if (_.isEmpty(tree)) {
      return;
    }

    if (input === '') {
      setIsFiltered(false);

      const hasNoActiveItem = _.isEmpty(activeItems);

      if (hasNoActiveItem) {
        return setFilteredItems(_.compact(internalTree));
      }

      const filtered = _.cloneDeep(internalTree).map(
        R.compose(
          TreeFuncs.map(updateExpand(true)),
        )
      );
      setFilteredItems(_.compact(filtered));
    } else {
      // TODO we should redo search as we have assetDescendents to searh one time instead of iterating through the tree
      // const lcInput = input.toLowerCase();
      // const found = _.filter(internalTree[0].assetDescendants, asset => {
      //   if (asset?.displayName) {
      //     return (asset.displayName.toLowerCase().indexOf(lcInput) !== -1);
      //   } else if (asset?.name) {
      //     return (asset.name.toLowerCase().indexOf(lcInput) !== -1);
      //   }
      //   return false;
      // });
      const filtered = _.cloneDeep(internalTree).map(
        R.compose(
          TreeFuncs.map(updateExpand(true)),
          TreeFuncs.filter(filterNodeName(input.toLowerCase()))
        )
      );
      setIsFiltered(true);
      setFilteredItems(_.compact(filtered));
    }
  }, [activeItems, internalTree, tree]);


  const toolbar = (
    <Toolbar style={{ padding: 0 }}>
      <ToolbarContent style={{ padding: 0 }}>
        <ToolbarItem width={{ default: '100%' }} className="toolbar-item">
          <TreeViewSearch
            className="toolbar-item-search"
            id="input-search"
            name="search-input"
            aria-label="Tree Search"
            onSearch={onTreeSearch}
          />
        </ToolbarItem>
      </ToolbarContent>
    </Toolbar>
  );

  // if (isLoading || (filteredItems.length === 0)) {
  if (isLoading || !initialProcessingReady) {
    return <Skeleton style={{ margin: '10px' }} shape="square" width="220px" screenreaderText="Loading contents" />;
  }

  return (
    <TreeView
      data={filteredItems}
      activeItems={activeItems}
      onSelect={_onSelect}
      variant="default"
      hasGuides
      toolbar={toolbar}
      hasCheckboxes={selectMode === SELECT_MODE.MULTI}
      onCheck={onCheck}
      hasSelectableNodes
    />
  );

};

Tree.propTypes = {
  tree: PropTypes.arrayOf(
    RecursiveShapeType({
      name: PropTypes.string.isRequired,
      id: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      isDisabled: PropTypes.bool,
      description: PropTypes.string
    })),
  activeItems: PropTypes.array,
  isLoading: PropTypes.bool,
  relationFilter: PropTypes.arrayOf(PropTypes.string),
  // TODO: add validation to funcs as well https://developer.fortnox.se/blog/proptypes-in-react-js/
  // https://stackoverflow.com/questions/33278862/reactjs-component-proptypes-specify-a-function-type-with-a-set-number-paramete
  onSelect: PropTypes.func,
  selectMode: PropTypes.string
};

export default React.memo(Tree);

